"use client";

import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { IMAGE_FILE_TYPES, VIDEO_FILE_TYPES } from "@/libs/builder/consts";
import { getBuilderModelName } from "@/libs/builder/tools/getBuilderModelName";

import { DEFAULT_STEPPER_PROGRESS_LABEL } from "./constants";

const UserAuth = dynamic(async () => (await import("./UserAuth")).UserAuth, {
  ssr: false,
});

export const userAuthInputs = [
  {
    name: "uxRevamp",
    type: "object",
    defaultValue: {
      authEntryInformationalText: "",
      authHighlights: [],
      isLoginRevampEnabled: false,
      isOtpSendAuto: false,
      defaultCountryCode: "",
    },
    subFields: [
      {
        name: "isLoginRevampEnabled",
        type: "boolean",
        friendlyName: "Is Login Revamp Enabled",
      },
      {
        name: "authHighlights",
        type: "list",
        defaultValue: [],
        showIf: `options.get("isLoginRevampEnabled") === true`,
        subFields: [
          {
            name: "value",
            type: "string",
            friendlyName: "Highlight Text",
          },
        ],
      },
      {
        name: "isOtpSendAuto",
        type: "boolean",
        friendlyName: "Is OTP Send Auto Enabled",
      },
      {
        name: "authEntryInformationalText",
        type: "string",
        friendlyName: "Auth Entry Informational Text",
        showIf: `options.get("isLoginRevampEnabled") === true`,
      },
      {
        name: "defaultCountryCode",
        type: "string",
        friendlyName: "Default country for phone number picker",
        enum: [
          "AC",
          "AD",
          "AE",
          "AF",
          "AG",
          "AI",
          "AL",
          "AM",
          "AO",
          "AR",
          "AS",
          "AT",
          "AU",
          "AW",
          "AX",
          "AZ",
          "BA",
          "BB",
          "BD",
          "BE",
          "BF",
          "BG",
          "BH",
          "BI",
          "BJ",
          "BL",
          "BM",
          "BN",
          "BO",
          "BQ",
          "BR",
          "BS",
          "BT",
          "BW",
          "BY",
          "BZ",
          "CA",
          "CC",
          "CD",
          "CF",
          "CG",
          "CH",
          "CI",
          "CK",
          "CL",
          "CM",
          "CN",
          "CO",
          "CR",
          "CU",
          "CV",
          "CW",
          "CX",
          "CY",
          "CZ",
          "DE",
          "DJ",
          "DK",
          "DM",
          "DO",
          "DZ",
          "EC",
          "EE",
          "EG",
          "EH",
          "ER",
          "ES",
          "ET",
          "FI",
          "FJ",
          "FK",
          "FM",
          "FO",
          "FR",
          "GA",
          "GB",
          "GD",
          "GE",
          "GF",
          "GG",
          "GH",
          "GI",
          "GL",
          "GM",
          "GN",
          "GP",
          "GQ",
          "GR",
          "GT",
          "GU",
          "GW",
          "GY",
          "HK",
          "HN",
          "HR",
          "HT",
          "HU",
          "ID",
          "IE",
          "IL",
          "IM",
          "IN",
          "IO",
          "IQ",
          "IR",
          "IS",
          "IT",
          "JE",
          "JM",
          "JO",
          "JP",
          "KE",
          "KG",
          "KH",
          "KI",
          "KM",
          "KN",
          "KP",
          "KR",
          "KW",
          "KY",
          "KZ",
          "LA",
          "LB",
          "LC",
          "LI",
          "LK",
          "LR",
          "LS",
          "LT",
          "LU",
          "LV",
          "LY",
          "MA",
          "MC",
          "MD",
          "ME",
          "MF",
          "MG",
          "MH",
          "MK",
          "ML",
          "MM",
          "MN",
          "MO",
          "MP",
          "MQ",
          "MR",
          "MS",
          "MT",
          "MU",
          "MV",
          "MW",
          "MX",
          "MY",
          "MZ",
          "NA",
          "NC",
          "NE",
          "NF",
          "NG",
          "NI",
          "NL",
          "NO",
          "NP",
          "NR",
          "NU",
          "NZ",
          "OM",
          "PA",
          "PE",
          "PF",
          "PG",
          "PH",
          "PK",
          "PL",
          "PM",
          "PR",
          "PS",
          "PT",
          "PW",
          "PY",
          "QA",
          "RE",
          "RO",
          "RS",
          "RU",
          "RW",
          "SA",
          "SB",
          "SC",
          "SD",
          "SE",
          "SG",
          "SH",
          "SI",
          "SJ",
          "SK",
          "SL",
          "SM",
          "SN",
          "SO",
          "SR",
          "SS",
          "ST",
          "SV",
          "SX",
          "SY",
          "SZ",
          "TA",
          "TC",
          "TD",
          "TG",
          "TH",
          "TJ",
          "TK",
          "TL",
          "TM",
          "TN",
          "TO",
          "TR",
          "TT",
          "TV",
          "TW",
          "TZ",
          "UA",
          "UG",
          "US",
          "UY",
          "UZ",
          "VA",
          "VC",
          "VE",
          "VG",
          "VI",
          "VN",
          "VU",
          "WF",
          "WS",
          "XK",
          "YE",
          "YT",
          "ZA",
          "ZM",
          "ZW",
        ],
      },
    ],
  },
  {
    name: "consents",
    type: "object",
    defaultValue: {
      oneTrustErrorMessage: "",
      oneTrustErrorLinkText: "",
      oneTrustErrorRedirectUrl: "",
      legalConditionsForConsents: "",
      consentOptionsListType: "default",
      consentsGroupingType: "default",
      acceptAllButtonMode: "none",
      singleOptionConsentDefaultValue: "true",
      isOptionalConsentsRequired: true,
      isColumnDisplayOfConsents: false,
    },
    subFields: [
      {
        name: "oneTrustErrorMessage",
        type: "richText",
      },
      {
        name: "oneTrustErrorLinkText",
        type: "string",
      },
      {
        name: "oneTrustErrorRedirectUrl",
        type: "url",
        required: true,
      },
      {
        name: "legalConditionsForConsents",
        type: "string",
        required: false,
        defaultValue: "",
      },
      {
        name: "isOptionalConsentsRequired",
        friendlyName:
          "Is optional consents required? (validates if user has selected option for each optional consent)",
        type: "boolean",
        defaultValue: true,
      },
      {
        name: "isColumnDisplayOfConsents",
        friendlyName: "Display style of consents (row/column)",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "consentOptionsListType",
        type: "string",
        enum: ["default", "extended"],
      },
      {
        name: "singleOptionConsentDefaultValue",
        friendlyName: "Default value for single option consent (not applicable for other options)",
        type: "string",
        defaultValue: "true",
        enum: ["blank", "true", "false"],
      },
      {
        name: "consentsGroupingType",
        type: "string",
        enum: ["default", "section", "single_option"],
      },
      {
        name: "acceptAllButtonMode",
        type: "string",
        enum: ["none", "optional"],
      },
    ],
  },
  {
    name: "ageVerificationMethodImages",
    type: "reference",
    model: getBuilderModelName("age-verification-method-images"),
  },
  {
    name: "deviceRegistration",
    type: "object",
    defaultValue: {
      isDeviceRegistrationByLeafletEnabled: false,
      isScanDeviceQrEnabled: false,
      deviceCodeLength: "short",
      isRegistrationLabelEnabled: false,
      isOverrideHelperRegistrationText: false,
    },
    subFields: [
      {
        name: "isDeviceRegistrationByLeafletEnabled",
        type: "boolean",
      },
      {
        name: "isScanDeviceQrEnabled",
        type: "boolean",
      },
      {
        name: "deviceCodeLength",
        type: "object",
        subFields: [
          {
            name: "short",
            type: "boolean",
            friendlyName: "Short code",
          },
          {
            name: "long",
            type: "boolean",
            friendlyName: "Long code",
          },
          {
            name: "gen4",
            type: "boolean",
            friendlyName: "Gen 4 code",
          },
        ],
        defaultValue: {
          short: true,
          long: false,
          gen4: false,
        },
        required: true,
      },
      {
        name: "desktopVideoTutorialUrl",
        type: "file",
        allowedFileTypes: VIDEO_FILE_TYPES,
        friendlyName: "Desktop video tutorial",
      },
      {
        name: "mobileVideoTutorialUrl",
        type: "file",
        friendlyName: "Mobile video tutorial",
        allowedFileTypes: VIDEO_FILE_TYPES,
      },
      {
        name: "posterTutorialUrl",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "isVideoTutorialSectionHidden",
        friendlyName: "Hide video tutorial section",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "isRegistrationLabelEnabled",
        friendlyName: "Enable registration label",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "isOverrideHelperRegistrationText",
        friendlyName: "Enables overriding text for the device registration input",
        type: "boolean",
        defaultValue: false,
      },
    ],
  },
  {
    name: "genderInformation",
    type: "object",
    subFields: [
      {
        name: "isGenderEnabled",
        type: "boolean",
      },
    ],
  },
  {
    name: "registrationSuccessPage",
    type: "object",
    subFields: [
      {
        name: "successBannerMessage",
        type: "richText",
      },
      {
        name: "hidePloomClubBanner",
        type: "boolean",
      },
      {
        name: "learnMoreLinkText",
        type: "string",
      },
      {
        name: "disclaimer",
        type: "string",
      },
      {
        name: "learnMoreLinkUrl",
        type: "url",
      },
    ],
  },
  {
    name: "loyaltyCardSuccessPage",
    type: "object",
    subFields: [
      {
        name: "isLoyaltyCardSuccessPageEnabled",
        type: "boolean",
        required: true,
      },
      {
        name: "isPloomBenefitsBannerHidden",
        type: "boolean",
        required: true,
      },
      {
        name: "clubCardLogo",
        friendlyName: "Club Card logo",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
    ],
  },
  {
    name: "offlineRegistration",
    type: "object",
    subFields: [
      {
        name: "isAvEnabled",
        type: "boolean",
        friendlyName: "Is AV enabled for NON AV register flow?",
      },
      {
        name: "isDeviceRegistrationEnabled",
        type: "boolean",
        friendlyName: "Is device registration step enabled for NON AV register flow?",
      },
    ],
  },
  {
    name: "registrationCommon",
    type: "object",
    subFields: [
      {
        name: "isRegistrationClosableBeforeAv",
        type: "boolean",
      },
      {
        name: "isPostCodeNotRequired",
        type: "boolean",
      },
      {
        name: "isAutocompleteDisabled",
        type: "boolean",
      },
      {
        name: "isAgeVerificationUuidFromCookieRequired",
        type: "boolean",
      },
      {
        name: "avDisclaimerMessage",
        type: "richText",
      },
      {
        name: "isAdditionalPhoneValidationEnabled",
        type: "boolean",
      },
      {
        name: "progressLabel",
        type: "string",
        friendlyName: "Stepper progress label (accessibility)",
        defaultValue: DEFAULT_STEPPER_PROGRESS_LABEL,
      },
    ],
  },
  {
    name: "phoneValidationOtp",
    type: "object",
    subFields: [
      {
        name: "successValidationViaSmsMessage",
        type: "richText",
      },
      {
        name: "failedValidationViaSmsMessage",
        type: "richText",
      },
      {
        name: "isOtpNumeric",
        type: "boolean",
      },
    ],
  },
  {
    name: "emailValidation",
    type: "object",
    subFields: [
      {
        name: "isOtpNumeric",
        type: "boolean",
      },
    ],
  },
  {
    name: "commonLoginSettings",
    type: "object",
    subFields: [
      {
        name: "socialLoginSeparatorText",
        type: "string",
      },
      {
        name: "loginFormMode",
        type: "string",
        required: true,
        enum: ["email", "phone", "email_and_phone"],
        defaultValue: "email",
      },
      {
        name: "loginFormModeDefaultTab",
        type: "string",
        required: true,
        enum: ["email", "phone"],
        defaultValue: "email",
      },
      {
        name: "isKeepMeLoggedInEnabled",
        type: "boolean",
        defaultValue: false,
      },
    ],
  },
] as const satisfies Inputs;

export const UserAuthComponent: RegisteredComponent = {
  name: "UserAuth",
  component: UserAuth,
  inputs: userAuthInputs,
};
